import { FC, ReactNode } from 'react';
import { Card, Chip, Stack, Typography } from '@mui/material';

import { LocalizationTypography } from '~components/organisms/localization';
import { PricingDisplayModel, PricingRateType } from '~types/pricing.type';

const CheckItem: React.FC<{ text?: string | null }> = ({ text }) => {
  if (!text) {
    return null;
  }

  return (
    <Stack direction="row" alignItems="center" gap={1}>
      <img src="/images/blue-round-check.svg" alt="check" width={20} />
      <Typography variant="Poppins/Body-md">{text}</Typography>
    </Stack>
  );
};

const rentalCardStyle = {
  width: '100%',
  px: 2,
  py: 1.5,
  border: '1px solid',
  borderColor: 'grey.300',
  borderRadius: 1.5,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  cursor: 'pointer',
  boxShadow: '0 2px 4px rgba(18, 28, 45, 0.05)',
  transition:
    'background-color 0.2s ease, transform 0.1s ease, box-shadow 0.2s ease, border-color 0.2s ease',
  '&:hover': {
    backgroundColor: 'primary.alpha4',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 16px rgba(15, 23, 42, 0.12)',
  },
  '&:active': {
    transform: 'translateY(0)',
    boxShadow: '0 4px 8px rgba(15, 23, 42, 0.12)',
  },
};

interface PricingOptionProps {
  displayModel: PricingDisplayModel;
  onSelect: () => void;
  isSelected: boolean;
  /**
   * Rendered next to the title, e.g. the "Best Value" tag on the recurring
   * pass.
   */
  badge?: ReactNode;
  /**
   * Overrides the default lost-fee notice in the card footer. The pass catalog
   * ships its own copy per pricing.
   */
  notice?: ReactNode;
}

function getPricingInstruction(displayModel: PricingDisplayModel) {
  if (displayModel.rateType === PricingRateType.FLAT) {
    return `${displayModel.pricingInstruction} ${displayModel.maxFeeInstructionV2}`;
  }
  return `${displayModel.pricingInstruction} • ${displayModel.maxFeeInstructionV2}`;
}

export const PricingOption: FC<PricingOptionProps> = ({
  displayModel,
  onSelect,
  isSelected,
  badge,
  notice,
}) => {
  return (
    <Card
      sx={{
        ...rentalCardStyle,
        ...(isSelected && {
          borderColor: 'brand.main',
          backgroundColor: 'primary.alpha4',
          boxShadow: '0 8px 16px rgba(0, 158, 250, 0.25)',
        }),
      }}
      onClick={onSelect}
    >
      <Stack gap={1}>
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          <Typography variant="Poppins/Heading-4">
            {displayModel.title}
          </Typography>
          {badge}
        </Stack>
        <Typography variant="Poppins/Subheading-lg" color="brand.dark">
          {getPricingInstruction(displayModel)}
        </Typography>
      </Stack>

      <Stack>
        <CheckItem text={displayModel.rentChargerInstruction} />
        <CheckItem text={displayModel.rentTimeInstruction} />
      </Stack>

      {notice ?? (
        <LocalizationTypography
          textId="app.rentalPage.newStepThree.selectRentalOption.notice"
          defaultText="{{symbol}}Return charger to avoid lost fees."
          textValues={{ symbol: '*' }}
          variant="Poppins/Body-sm"
        />
      )}
    </Card>
  );
};

/**
 * The "Best Value" tag on the recurring pass in the catalog.
 */
export const BestValueBadge: FC = () => (
  <Chip
    size="small"
    label={
      <LocalizationTypography
        textId="app.rentalPage.newStepThree.selectRentalOption.bestValue"
        defaultText="Best Value"
        variant="Poppins/Subheading-xs"
        color="brand.dark"
      />
    }
    sx={{
      backgroundColor: 'brand.lightest',
      borderRadius: 50,
      height: 'auto',
      py: 0.5,
      '& .MuiChip-label': { px: 1.25 },
    }}
  />
);
